// src/pages/Legal/PrivacidadePage.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Card,
  CardContent,
  CardMedia,
  IconButton,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  DataUsage as DataUsageIcon,
  DeleteOutline as DeleteIcon,
  Update as UpdateIcon,
  Share as ShareIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Shield as ShieldIcon,
  Gavel as GavelIcon,
  VerifiedUser as VerifiedUserIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Footer from '../../components/Footer/Footer';

// Componente para seções de privacidade
const PrivacySection = ({ title, icon: Icon, children }) => (
  <Paper sx={{ p: 3, mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Icon color="primary" sx={{ mr: 2, fontSize: 28 }} />
      <Typography variant="h6">{title}</Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

PrivacySection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

// Dados dos departamentos (não usado aqui, mas mantido para consistência)
const departments = [
  {
    id: 1,
    name: "Desenvolvimento",
    members: [
      {
        name: "Lerion Viegas",
        role: "Lead Developer",
        image: "/path/to/image.jpg"
      },
      {
        name: "Elias Manecas",
        role: "Frontend Developer",
        image: "/path/to/image.jpg"
      },
      // Adicione mais membros conforme necessário
    ]
  },
  {
    id: 2,
    name: "Suporte ao Cliente",
    members: [
      {
        name: "Elias Manecas",
        role: "Customer Success Manager",
        image: "/path/to/image.jpg"
      },
      // Adicione mais membros conforme necessário
    ]
  },
  // Adicione mais departamentos conforme necessário
];

// Dados dos direitos do usuário
const direitosUsuario = [
  {
    titulo: 'Acesso aos Dados',
    descricao: 'Direito de acessar todos os dados pessoais armazenados sobre você.'
  },
  {
    titulo: 'Retificação',
    descricao: 'Possibilidade de corrigir dados incorretos ou desatualizados.'
  },
  {
    titulo: 'Exclusão',
    descricao: 'Direito de solicitar a exclusão dos seus dados, respeitando as obrigações legais.'
  },
  {
    titulo: 'Portabilidade',
    descricao: 'Direito de receber seus dados em formato estruturado.'
  }
];

const PrivacidadePage = () => {
  const dadosColetados = [
    {
      tipo: 'Estudantes',
      dados: [
        'Nome completo',
        'Número de documento de identificação',
        'Endereço de email',
        'Número de telefone',
        'Endereço residencial',
        'Histórico acadêmico',
        'Dados de pré-inscrições',
        'Informações de pagamento'
      ]
    },
    {
      tipo: 'Instituições',
      dados: [
        'Nome da instituição',
        'NUIT',
        'Endereço',
        'Contactos administrativos',
        'Informações das campanhas',
        'Dados dos cursos oferecidos',
        'Registros de transações'
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom>
            Política de Privacidade
          </Typography>
          <Typography variant="h6">
            Protecção e transparência no tratamento dos seus dados
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserIcon />
            <Typography variant="subtitle1">
              Última actualização: Novembro 2024
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1, mb: 4 }}>
        {/* Introdução */}
        <Alert severity="info" sx={{ mb: 4 }}>
          Esta política de privacidade explica como o ZenKroll colecta, usa, protege e gere seus dados pessoais em conformidade com a legislação aplicável.
        </Alert>

        {/* Dados Coletados */}
        <PrivacySection title="Dados que Coletamos" icon={DataUsageIcon}>
          <Typography paragraph>
            Coletamos diferentes tipos de dados dependendo do seu perfil de usuário:
          </Typography>
          
          {dadosColetados.map((categoria) => (
            <Accordion key={categoria.tipo}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {categoria.tipo === 'Estudantes' ? <PersonIcon sx={{ mr: 1 }} /> : <SchoolIcon sx={{ mr: 1 }} />}
                  <Typography>{categoria.tipo}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {categoria.dados.map((dado, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <DataUsageIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={dado} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </PrivacySection>

        {/* Uso dos Dados */}
        <PrivacySection title="Como Usamos seus Dados" icon={SecurityIcon}>
          <List>
            <ListItem>
              <ListItemIcon><LockIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Gestão de Pré-inscrições"
                secondary="Processamento e acompanhamento das suas solicitações de pré-inscrição"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Pagamentos"
                secondary="Processamento seguro de pagamentos e gestão financeira"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><UpdateIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Comunicações"
                secondary="Envio de actualizações sobre suas pré-inscrições e campanhas"
              />
            </ListItem>
          </List>
        </PrivacySection>

        {/* Compartilhamento de Dados */}
        <PrivacySection title="Compartilhamento de Dados" icon={ShareIcon}>
          <Typography paragraph>
            Compartilhamos seus dados apenas com:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><SchoolIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Instituições de Ensino"
                secondary="Apenas para as instituições onde você realizou pré-inscrição"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Processadores de Pagamento"
                secondary="Parceiros seguros para processamento de pagamentos"
              />
            </ListItem>
          </List>
        </PrivacySection>

        {/* Direitos do Usuário */}
        <PrivacySection title="Seus Direitos" icon={GavelIcon}>
          <Typography paragraph>
            Como titular dos dados, você tem os seguintes direitos:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {direitosUsuario.map((direito) => (
              <Paper 
                key={direito.titulo}
                sx={{ 
                  p: 2, 
                  flex: '1 1 300px',
                  bgcolor: 'primary.soft',
                  border: 1,
                  borderColor: 'primary.light'
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {direito.titulo}
                </Typography>
                <Typography variant="body2">
                  {direito.descricao}
                </Typography>
              </Paper>
            ))}
          </Box>
        </PrivacySection>

        {/* Segurança */}
        <PrivacySection title="Segurança dos Dados" icon={ShieldIcon}>
          <Typography paragraph>
            Implementamos medidas técnicas e organizacionais para proteger seus dados:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><LockIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Criptografia"
                secondary="Todos os dados são criptografados em trânsito e em repouso"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Controle de Acesso"
                secondary="Acesso restrito apenas a pessoal autorizado"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedUserIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Monitoramento"
                secondary="Sistemas de detecção e prevenção de invasões"
              />
            </ListItem>
          </List>
        </PrivacySection>

        {/* Período de Retenção */}
        <PrivacySection title="Período de Retenção" icon={DeleteIcon}>
          <Typography paragraph>
            Mantemos seus dados pelo tempo necessário para:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><DataUsageIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Dados de Pré-inscrição"
                secondary="Durante todo o processo de pré-inscrição e por 2 anos após sua conclusão"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Dados Financeiros"
                secondary="Conforme exigido pela legislação fiscal (5 anos)"
              />
            </ListItem>
          </List>
        </PrivacySection>

        {/* Contato */}
        <PrivacySection title="Contacto para Assuntos de Privacidade" icon={PersonIcon}>
          <Typography paragraph>
            Para exercer seus direitos ou esclarecer dúvidas sobre privacidade:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Email: privacidade@zenkroll.co.mz" />
            </ListItem>
            <ListItem>
              <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Encarregado de Proteção de Dados: Danilo Taju" />
            </ListItem>
          </List>
        </PrivacySection>
      </Container>

      <Footer />
    </Box>
  );
};

PrivacidadePage.propTypes = {
  // Nenhuma prop recebida diretamente pelo componente
};

export default PrivacidadePage;
